'use client';

import React, { useState } from 'react';
import AppImage from '@/components/ui/AppImage';
import Breadcrumbs from '@/components/Breadcrumbs';
import CopyPromptButton from '@/components/CopyPromptButton';
import PromptCard from '@/components/PromptCard';
import FAQAccordion from '@/components/FAQAccordion';
import { getRelatedPrompts } from '@/data/prompts';
import { getCategoryBySlug } from '@/data/categories';
import {
  Tag, Wrench, Lightbulb, CheckCircle2, Target,
  ChevronRight, BookOpen, Zap, ArrowRight, Info
} from 'lucide-react';
import Link from 'next/link';
import type { Prompt } from '@/data/prompts';

interface PromptDetailViewProps {
  prompt: Prompt;
}

const aiToolColors: Record<string, string> = {
  'Midjourney v6': 'bg-blue-50 text-blue-700 border-blue-200',
  'DALL·E 3': 'bg-green-50 text-green-700 border-green-200',
  'Stable Diffusion': 'bg-purple-50 text-purple-700 border-purple-200',
  'Stable Diffusion XL': 'bg-purple-50 text-purple-700 border-purple-200',
};

export default function PromptDetailView({ prompt }: PromptDetailViewProps) {
  const [activeCustomVar, setActiveCustomVar] = useState(0);
  const relatedPrompts = getRelatedPrompts(prompt.slug, 6);

  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: prompt.title,
    description: prompt.seoDescription,
    url: `https://pinuprompt.site/prompt/${prompt.slug}`,
    image: prompt.image,
    keywords: prompt.tags.join(', '),
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10">
        {/* Breadcrumbs */}
        <div className="py-4">
          <Breadcrumbs
            items={[
              { label: 'Prompts', href: '/prompt' },
              ...(prompt.categorySlugs[0]
                ? [{
                    label: getCategoryBySlug(prompt.categorySlugs[0])?.name || prompt.categorySlugs[0],
                    href: `/categories/${prompt.categorySlugs[0]}`,
                  }]
                : []),
              { label: prompt.title },
            ]}
          />
        </div>

        {/* Main 2-Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-5 xl:grid-cols-5 2xl:grid-cols-5 gap-8 mb-12">
          {/* Left: Image + Meta */}
          <div className="lg:col-span-2 space-y-5">
            {/* Preview Image */}
            <div className="relative aspect-[4/3] rounded-2xl overflow-hidden shadow-lg">
              <AppImage
                src={prompt.image}
                alt={`AI generated image preview for: ${prompt.title} — ${prompt.style} style using prompt about ${prompt.shortDescription}`}
                fill
                priority
                sizes="(max-width: 1024px) 100vw, 40vw"
                className="object-cover"
              />
              <div className="absolute top-3 left-3">
                <span className="badge-pink text-xs">{prompt.style}</span>
              </div>
              {prompt.trending && (
                <div className="absolute top-3 right-3">
                  <span className="bg-primary text-white text-xs font-bold px-2.5 py-1 rounded-full">
                    Trending
                  </span>
                </div>
              )}
            </div>

            {/* Meta Card */}
            <div className="bg-white rounded-2xl border border-border p-5 shadow-sm">
              <h3 className="font-semibold text-sm text-foreground mb-4">Prompt Details</h3>
              <div className="space-y-3">
                <div>
                  <p className="text-xs text-muted-foreground mb-1.5 uppercase tracking-wider font-medium">Categories</p>
                  <div className="flex flex-wrap gap-1.5">
                    {prompt.categorySlugs.map((slug) => {
                      const cat = getCategoryBySlug(slug);
                      return (
                        <Link
                          key={`detail-cat-${slug}`}
                          href={`/categories/${slug}`}
                          className="badge-pink text-xs hover:bg-pink-200 transition-colors"
                        >
                          {cat?.emoji} {cat?.name || slug}
                        </Link>
                      );
                    })}
                  </div>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1.5 uppercase tracking-wider font-medium">Works Best With</p>
                  <div className="flex flex-wrap gap-1.5">
                    {prompt.aiTools.map((tool) => (
                      <span
                        key={`tool-${tool}`}
                        className={`text-xs font-medium px-2.5 py-1 rounded-full border ${aiToolColors[tool] || 'bg-muted text-muted-foreground border-border'}`}
                      >
                        {tool}
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1.5 uppercase tracking-wider font-medium">Tags</p>
                  <div className="flex flex-wrap gap-1.5">
                    {prompt.tags.map((tag) => (
                      <span key={`tag-${tag}`} className="badge-muted text-xs">
                        #{tag}
                      </span>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1 uppercase tracking-wider font-medium">Prompt Length</p>
                  <p className="text-sm font-semibold text-foreground">{prompt.prompt.split(' ').length} words</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Prompt + Details */}
          <div className="lg:col-span-3 space-y-6">
            {/* Title */}
            <div>
              <h1 className="text-2xl sm:text-3xl font-extrabold text-foreground leading-tight mb-2">
                {prompt.title}
              </h1>
              <p className="text-muted-foreground text-sm leading-relaxed">{prompt.shortDescription}</p>
            </div>

            {/* Prompt Block */}
            <div className="bg-white rounded-2xl border-2 border-border overflow-hidden shadow-sm">
              <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-muted/30">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-400" />
                  <div className="w-3 h-3 rounded-full bg-yellow-400" />
                  <div className="w-3 h-3 rounded-full bg-green-400" />
                </div>
                <span className="text-xs text-muted-foreground font-medium">AI Image Prompt</span>
                <CopyPromptButton prompt={prompt.prompt} variant="icon" />
              </div>
              <div className="p-5">
                <p className="font-mono text-sm leading-relaxed text-foreground whitespace-pre-wrap break-words">
                  {prompt.prompt}
                </p>
              </div>
            </div>

            {/* Copy Button */}
            <CopyPromptButton prompt={prompt.prompt} variant="large" />

            {/* About */}
            <div className="bg-white rounded-2xl border border-border p-5 shadow-sm">
              <div className="flex items-center gap-2 mb-3">
                <Info size={16} className="text-primary" />
                <h2 className="font-bold text-foreground text-sm">About This Prompt</h2>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">{prompt.aboutPrompt}</p>
            </div>

            {/* What It Creates */}
            <div className="bg-white rounded-2xl border border-border p-5 shadow-sm">
              <div className="flex items-center gap-2 mb-3">
                <BookOpen size={16} className="text-primary" />
                <h2 className="font-bold text-foreground text-sm">What This Prompt Creates</h2>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">{prompt.whatItCreates}</p>
            </div>
          </div>
        </div>

        {/* Use Cases */}
        <section className="mb-10" aria-labelledby="use-cases-heading">
          <h2 id="use-cases-heading" className="text-xl font-bold text-foreground mb-5 flex items-center gap-2">
            <Target size={18} className="text-primary" />
            Best Use Cases
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4 2xl:grid-cols-4 gap-4">
            {prompt.useCases.map((useCase, i) => (
              <div
                key={`use-case-${i}`}
                className="bg-white rounded-2xl border border-border p-5 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-200"
              >
                <div className="w-8 h-8 rounded-xl bg-accent flex items-center justify-center mb-3">
                  <Zap size={15} className="text-primary" />
                </div>
                <h3 className="font-semibold text-foreground text-sm mb-1.5">{useCase.title}</h3>
                <p className="text-xs text-muted-foreground leading-relaxed">{useCase.description}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Customization */}
        <section className="mb-10 bg-gradient-to-br from-accent to-pink-100 rounded-2xl border border-pink-200 p-6 lg:p-8">
          <div className="flex items-center gap-2 mb-5">
            <Wrench size={18} className="text-primary" />
            <h2 className="text-xl font-bold text-foreground">Customize This Prompt</h2>
          </div>
          <p className="text-sm text-muted-foreground mb-6">
            These key variables can be swapped to create unique variations. Click a variable to see suggestions.
          </p>
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-2 gap-6">
            {/* Variable selector */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-3 uppercase tracking-wider">Variables</p>
              <div className="space-y-2">
                {prompt.customizationTips.map((tip, i) => (
                  <button
                    key={`custom-var-${i}`}
                    onClick={() => setActiveCustomVar(i)}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border text-left transition-all ${
                      activeCustomVar === i
                        ? 'border-primary bg-white shadow-sm text-primary'
                        : 'border-border bg-white/50 text-foreground hover:border-primary/40'
                    }`}
                  >
                    <div className="flex items-center gap-2">
                      <Tag size={14} className={activeCustomVar === i ? 'text-primary' : 'text-muted-foreground'} />
                      <span className="font-medium text-sm">{tip.variable}</span>
                    </div>
                    <ChevronRight size={14} className={`transition-transform ${activeCustomVar === i ? 'rotate-90 text-primary' : 'text-muted-foreground'}`} />
                  </button>
                ))}
              </div>
            </div>
            {/* Suggestions for selected variable */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-3 uppercase tracking-wider">
                Suggestions for "{prompt.customizationTips[activeCustomVar]?.variable}"
              </p>
              <div className="space-y-2">
                {prompt.customizationTips[activeCustomVar]?.suggestions.map((suggestion, i) => (
                  <div
                    key={`suggestion-${activeCustomVar}-${i}`}
                    className="flex items-center gap-2.5 bg-white rounded-xl border border-border px-4 py-3"
                  >
                    <CheckCircle2 size={14} className="text-primary flex-shrink-0" />
                    <span className="text-sm text-foreground">{suggestion}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Tips for Better Results */}
        <section className="mb-10">
          <h2 className="text-xl font-bold text-foreground mb-5 flex items-center gap-2">
            <Lightbulb size={18} className="text-primary" />
            Tips for Better Results
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {prompt.resultTips.map((tip, i) => (
              <div key={`result-tip-${i}`} className="flex items-start gap-3 bg-white rounded-xl border border-border p-4 shadow-sm">
                <span className="w-6 h-6 rounded-full bg-primary text-white text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
                  {i + 1}
                </span>
                <p className="text-sm text-foreground leading-relaxed">{tip}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Related Prompts */}
        {relatedPrompts.length > 0 && (
          <section className="mb-10">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-xl font-bold text-foreground">Related Prompts</h2>
              <Link
                href="/prompt"
                className="flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-primary-dark transition-colors"
              >
                Browse all
                <ArrowRight size={14} />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-3 gap-4">
              {relatedPrompts.map((related) => (
                <PromptCard key={`related-${related.id}`} prompt={related} variant="default" />
              ))}
            </div>
          </section>
        )}

        {/* FAQ */}
        <section className="mb-12 max-w-3xl">
          <FAQAccordion faqs={prompt.faqs} title={`FAQ — ${prompt.title}`} />
        </section>

        {/* Related Categories */}
        <section className="mb-12 pb-4 border-t border-border pt-8">
          <h2 className="text-lg font-bold text-foreground mb-4">Explore Related Categories</h2>
          <div className="flex flex-wrap gap-3">
            {prompt.categorySlugs.map((slug) => {
              const cat = getCategoryBySlug(slug);
              if (!cat) return null;
              return (
                <Link
                  key={`footer-cat-link-${slug}`}
                  href={`/categories/${slug}`}
                  className="flex items-center gap-2 px-4 py-2 rounded-full border border-border hover:border-primary hover:bg-accent hover:text-primary transition-all text-sm font-medium text-muted-foreground"
                >
                  {cat.emoji} {cat.name} Prompts
                  <ArrowRight size={13} />
                </Link>
              );
            })}
          </div>
        </section>
      </div>
    </>
  );
}